import { useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import {
  ChatbotRankingCriteria,
  type ChatbotRankingDTO,
  type LacTenantOverviewDateRange,
} from '../../types/lac';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { useLacTopChatbotsQuery } from '../../features/lac/queries/useLacTopChatbotsQuery';
import { sumAnalyticsRoleCounts } from '../../features/lac/sumAnalyticsRoleCounts';
import { useI18n } from '../../i18n/useI18n';
import { formatNumber } from '../../utils/formatUtils';
import { LacStatsTopSectionTitle } from './LacStatsTopSectionTitle';
import { LAC_CHATBOT_CRITERIA_OPTIONS } from '../../features/lac/lacChatbotsListParams';
import './LacTenantStatsKbCard.css';

type LacTenantStatsChatbotsSectionProps = {
  /** When omitted, ranks across all tenants. */
  tenantName?: string;
  knowledgeBaseId?: string | null;
  dateRange?: LacTenantOverviewDateRange;
  /** Show owning organisation on each card (typically when unscoped). */
  showTenant?: boolean;
  /** Controlled ranking criteria; uses internal state when omitted. */
  criteria?: ChatbotRankingCriteria;
  onCriteriaChange?: (criteria: ChatbotRankingCriteria) => void;
  /** Hide title + criteria dropdown (pass criteria from the parent toolbar). */
  hideHeader?: boolean;
  /** Max rankings to fetch; defaults to service limit (10). */
  limit?: number;
};

export function LacTenantStatsChatbotsSection({
  tenantName,
  knowledgeBaseId = null,
  dateRange,
  showTenant = false,
  criteria: controlledCriteria,
  onCriteriaChange,
  hideHeader = false,
  limit,
}: LacTenantStatsChatbotsSectionProps) {
  const { locale, translate } = useI18n();
  const [internalCriteria, setInternalCriteria] =
    useState<ChatbotRankingCriteria>(ChatbotRankingCriteria.Messages);

  const criteria = controlledCriteria ?? internalCriteria;
  const setCriteria = onCriteriaChange ?? setInternalCriteria;

  const topQuery = useLacTopChatbotsQuery(
    tenantName,
    criteria,
    knowledgeBaseId,
    dateRange,
    limit,
  );

  const rows = topQuery.data ?? [];
  const isLoading = topQuery.isLoading;
  const isError = topQuery.isError;

  const criteriaOptions = useMemo(
    () =>
      LAC_CHATBOT_CRITERIA_OPTIONS.map((value) => ({
        value,
        label: translate(`lac.organisations.stats.chatbots.criteria.${value}`),
      })),
    [translate],
  );

  return (
    <section
      className="lac-tenant-stats-section"
      aria-label={translate('lac.organisations.stats.chatbots.legend')}
    >
      {!hideHeader ? (
        <div className="lac-tenant-stats-section__header">
          <LacStatsTopSectionTitle>
            {translate('lac.organisations.stats.chatbots.title')}
          </LacStatsTopSectionTitle>
          <div className="lac-tenant-stats-section__criteria">
            <ArrowUpDown
              className="lac-tenant-stats-section__criteria-icon"
              strokeWidth={2}
              aria-hidden
            />
            <LiksDropdown
              className="lac-tenant-stats-section__criteria-dropdown"
              variant="on-light"
              value={criteria}
              onChange={(value) => setCriteria(value as ChatbotRankingCriteria)}
              options={criteriaOptions}
              aria-label={translate(
                'lac.organisations.stats.chatbots.criteriaLabel',
              )}
            />
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <p className="lac-tenant-overview__status">
          {translate('lac.organisations.stats.chatbots.loading')}
        </p>
      ) : null}

      {isError ? (
        <p className="lac-tenant-overview__status" role="alert">
          {translate('lac.organisations.stats.chatbots.loadError')}
        </p>
      ) : null}

      {!isLoading && !isError && rows.length === 0 ? (
        <p className="lac-tenant-overview__status">
          {translate('lac.organisations.stats.chatbots.empty')}
        </p>
      ) : null}

      {!isLoading && !isError && rows.length > 0 ? (
        <ul className="lac-tenant-stats-kb__grid">
          {rows.map((row, index) => (
            <li key={row.chatbotId ?? `chatbot-${index}`}>
              <ChatbotRankingCard
                row={row}
                locale={locale}
                showTenant={showTenant}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function ChatbotRankingCard({
  row,
  locale,
  showTenant,
}: {
  row: ChatbotRankingDTO;
  locale: string;
  showTenant: boolean;
}) {
  const { translate } = useI18n();
  const title =
    row.chatbotDisplayName?.trim() ||
    row.chatbotName?.trim() ||
    row.chatbotId?.trim() ||
    translate('lac.organisations.stats.chatbots.untitled');
  const knowledgeBaseName = row.knowledgeBaseName?.trim() ?? '';
  const memberCount = sumAnalyticsRoleCounts(row.membershipRoleCounts);
  const tenantText =
    row.tenantDisplayName?.trim() ||
    row.tenantName?.trim() ||
    translate('lac.chatbots.unknownTenant');

  return (
    <div className="lac-tenant-stats-kb__card">
      <div className="lac-tenant-stats-kb__card-main lac-tenant-stats-kb__card-main--static">
        <span className="lac-tenant-stats-kb__card-title">{title}</span>
        {showTenant ? (
          <span className="lac-tenant-stats-kb__card-tenant">{tenantText}</span>
        ) : null}
        <span
          className={[
            'lac-tenant-stats-kb__card-description',
            'lac-tenant-stats-kb__card-description--single-line',
            knowledgeBaseName === '' &&
              'lac-tenant-stats-kb__card-description--empty',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {knowledgeBaseName !== ''
            ? knowledgeBaseName
            : translate('lac.organisations.stats.chatbots.noKnowledgeBase')}
        </span>
        <dl className="lac-tenant-stats-kb__card-stats">
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>{translate('lac.organisations.stats.chatbots.members')}</dt>
            <dd>{formatNumber(memberCount, locale)}</dd>
          </div>
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate('lac.organisations.stats.chatbots.conversations')}
            </dt>
            <dd>{formatNumber(row.conversationCount, locale)}</dd>
          </div>
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>{translate('lac.organisations.stats.chatbots.messages')}</dt>
            <dd>{formatNumber(row.messageCount, locale)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
